import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Field, Notice } from "../components/ui";
import { LockupStacked } from "../components/Logo";

/**
 * Sign in with email and password.
 *
 * Every step happens inside the app. Password reset and signup
 * confirmation use a six-digit code rather than a link, because on iOS a
 * Home Screen app has its own storage: a link opened from Mail would
 * complete the reset in Safari and leave the app signed out.
 *
 * Stages:
 *   signin   email + password (default)
 *   signup   name + email + password
 *   confirm  code from the confirmation email
 *   forgot   email, to request a reset code
 *   reset    code + new password
 *
 * There is deliberately no passwordless sign-in. The only emailed codes are
 * for confirming a new account and resetting a forgotten password — without
 * the second, a forgotten password would lock someone out for good.
 */

const MIN = 8;

export default function SignIn() {
  const [params] = useSearchParams();
  /**
   * An invitation link carries everything but the password, so the person
   * lands on the last step rather than at a sign-in wall they then have
   * to find their way past.
   */
  const invited = params.get("invite") === "1";
  const invite = invited ? {
    email: params.get("email") ?? "",
    name: params.get("name") ?? "",
    code: params.get("code") ?? "",
    society: params.get("society") ?? "",
  } : null;

  const [stage, setStage] = useState(invited ? "signup" : "signin");
  const [editDetails, setEditDetails] = useState(false);   // "not you?"
  const [email, setEmail] = useState(invite?.email ?? "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [name, setName] = useState(invite?.name ?? "");
  const [token, setToken] = useState("");
  const [joinCode, setJoinCode] = useState(invite?.code ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const go = (next) => { setStage(next); setError(""); setInfo(""); setToken(""); };

  async function run(fn) {
    setError(""); setBusy(true);
    try { await fn(); }
    catch (err) { setError(friendly(err)); }
    finally { setBusy(false); }
  }

  /* ---------- sign in ---------- */
  const signIn = (e) => { e.preventDefault(); run(async () => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      if (/not confirmed/i.test(error.message)) {
        await supabase.auth.resend({ type: "signup", email });
        go("confirm");
        setInfo("Your account isn't confirmed yet. We've sent a fresh code.");
        return;
      }
      throw error;
    }
  }); };

  /* ---------- sign up ---------- */
  const signUp = (e) => { e.preventDefault(); run(async () => {
    if (password.length < MIN) throw new Error(`Use at least ${MIN} characters.`);
    if (password !== confirm) throw new Error("Those passwords don't match.");
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: {
        full_name: name.trim(),
        // read server-side by the invite-only trigger; a society can choose
        // to accept its code in place of an invitation
        join_code: joinCode.trim() || null,
      } },
    });
    if (error) throw error;

    // Supabase doesn't say "that email is taken" — it returns a user with
    // no identities and changes nothing, so nobody can probe which emails
    // are registered. Without this check the app reports success and the
    // password is never set. Common for anyone who first signed in with
    // Google, whose account has no password at all.
    if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      setStage("forgot");
      setInfo("There's already an account with that email. Set a password for it with a reset code below.");
      return;
    }
    // If email confirmation is off in Supabase, a session comes straight
    // back and the session listener takes over. Otherwise ask for the code.
    if (!data.session) go("confirm");
  }); };

  /* ---------- confirm signup ---------- */
  const confirmSignup = (e) => { e.preventDefault(); run(async () => {
    const t = token.trim();
    let { error } = await supabase.auth.verifyOtp({ email, token: t, type: "email" });
    if (error) ({ error } = await supabase.auth.verifyOtp({ email, token: t, type: "signup" }));
    if (error) throw error;
  }); };

  /* ---------- forgotten password ---------- */
  const requestReset = (e) => { e.preventDefault(); run(async () => {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) throw error;
    go("reset");
  }); };

  const doReset = (e) => { e.preventDefault(); run(async () => {
    if (password.length < MIN) throw new Error(`Use at least ${MIN} characters.`);
    if (password !== confirm) throw new Error("Those passwords don't match.");
    const { error: vErr } = await supabase.auth.verifyOtp({
      email, token: token.trim(), type: "recovery",
    });
    if (vErr) throw vErr;
    const { error: uErr } = await supabase.auth.updateUser({ password });
    if (uErr) throw uErr;
    // verifyOtp has signed them in; the session listener takes over
  }); };

  /* ---------- shared fields ---------- */
  const EmailField = (
    <Field label="Email">
      <input className="input" type="email" autoComplete="username" required
             inputMode="email" autoCapitalize="off" spellCheck="false"
             value={email} onChange={(e) => setEmail(e.target.value)}
             placeholder="you@example.com" />
    </Field>
  );
  const CodeField = (
    <Field label="Code">
      <input className="input code-input" required autoFocus inputMode="numeric"
             autoComplete="one-time-code" maxLength={8} value={token}
             onChange={(e) => setToken(e.target.value.replace(/\D/g, ""))}
             placeholder="123456" />
    </Field>
  );
  const NewPassword = (
    <>
      <Field label="New password" hint={`At least ${MIN} characters.`}>
        <input className="input" type="password" required minLength={MIN}
               autoComplete="new-password" value={password}
               onChange={(e) => setPassword(e.target.value)} />
      </Field>
      <Field label="Type it again">
        <input className="input" type="password" required minLength={MIN}
               autoComplete="new-password" value={confirm}
               onChange={(e) => setConfirm(e.target.value)} />
      </Field>
    </>
  );

  const Links = ({ children }) => (
    <div style={{ marginTop: "1.1rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
      {children}
    </div>
  );

  const screens = {
    signin: (
      <form onSubmit={signIn}>
        <h1>Sign in</h1>
        <p>Use the email your society has on file.</p>
        {EmailField}
        <Field label="Password">
          <input className="input" type="password" required autoComplete="current-password"
                 value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Notice kind="info">{info}</Notice>
        <Notice kind="error">{error}</Notice>
        <button className="btn btn-primary btn-block" disabled={busy} style={{ marginTop: ".6rem" }}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <Links>
          <button type="button" className="btn-link" onClick={() => go("forgot")}>
            Forgotten your password?
          </button>
          <button type="button" className="btn-link" onClick={() => go("signup")}>
            New here? Create an account
          </button>
        </Links>
      </form>
    ),

    signup: (
      <form onSubmit={signUp}>
        {invite ? (
          <>
            <h1>Welcome{invite.name ? `, ${invite.name.split(/\s+/)[0]}` : ""}</h1>
            <p>
              You've been added to <strong>{invite.society || "your society"}</strong>.
              Choose a password and you're in — everything else is already filled in.
            </p>
          </>
        ) : (
          <>
            <h1>Create an account</h1>
            <p>
              Accounts are by invitation. Use the same email address your
              organiser has on file for you — your scores will be waiting.
            </p>
          </>
        )}
        {invite && !editDetails ? (
          /*
           * These stay real inputs, read-only rather than plain text.
           * iOS Keychain and password managers need a username field in
           * the same form as the new password, or there's nothing for the
           * saved password to hang off and it never offers to save.
           *
           * readOnly, not disabled: a disabled field is dropped from the
           * form entirely and the managers ignore it.
           */
          <div className="prefilled">
            <label>
              <span className="eyebrow">Name</span>
              <input className="input flat" readOnly value={name}
                     autoComplete="name" name="name" />
            </label>
            <label>
              <span className="eyebrow">Email</span>
              <input className="input flat" readOnly value={email} type="email"
                     autoComplete="username" name="username" inputMode="email" />
            </label>
            <button type="button" className="btn-link"
                    onClick={() => setEditDetails(true)}>
              Not you?
            </button>
          </div>
        ) : (
          <>
            <Field label="Your name">
              <input className="input" required autoComplete="name" value={name}
                     onChange={(e) => setName(e.target.value)} placeholder="Will Grant" />
            </Field>
            {EmailField}
          </>
        )}
        {NewPassword}
        {(!invite || editDetails) && (
          <Field label="Society code" hint="Only if your organiser gave you one. Leave blank otherwise.">
            <input className="input code-input" maxLength={8} autoCapitalize="characters"
                   spellCheck="false" value={joinCode}
                   onChange={(e) => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                   placeholder="optional" />
          </Field>
        )}
        <Notice kind="error">{error}</Notice>
        <button className="btn btn-primary btn-block" disabled={busy} style={{ marginTop: ".6rem" }}>
          {busy ? "Creating…" : invite ? "Set my password and join" : "Create account"}
        </button>
        <Links>
          <button type="button" className="btn-link" onClick={() => go("signin")}>
            Already have an account? Sign in
          </button>
        </Links>
      </form>
    ),

    confirm: (
      <form onSubmit={confirmSignup}>
        <h1>Confirm your email</h1>
        <p>We've sent a six-digit code to <strong>{email}</strong>.</p>
        <Notice kind="info">{info}</Notice>
        {CodeField}
        <Notice kind="error">{error}</Notice>
        <button className="btn btn-primary btn-block" disabled={busy || token.length < 6}
                style={{ marginTop: ".6rem" }}>
          {busy ? "Checking…" : "Confirm"}
        </button>
        <Links>
          <button type="button" className="btn-link" disabled={busy}
                  onClick={() => run(async () => {
                    const { error } = await supabase.auth.resend({ type: "signup", email });
                    if (error) throw error;
                    setInfo("New code sent.");
                  })}>
            Send another code
          </button>
          <button type="button" className="btn-link" onClick={() => go("signin")}>
            Back to sign in
          </button>
        </Links>
      </form>
    ),

    forgot: (
      <form onSubmit={requestReset}>
        <h1>Set or reset your password</h1>
        <p>We'll email you a code. You'll set the new password right here.</p>
        <Notice kind="info">{info}</Notice>
        {EmailField}
        <Notice kind="error">{error}</Notice>
        <button className="btn btn-primary btn-block" disabled={busy} style={{ marginTop: ".6rem" }}>
          {busy ? "Sending…" : "Email me a reset code"}
        </button>
        <Links>
          <button type="button" className="btn-link" onClick={() => go("signin")}>
            Back to sign in
          </button>
        </Links>
      </form>
    ),

    reset: (
      <form onSubmit={doReset}>
        <h1>Choose a new password</h1>
        <p>Enter the code we sent to <strong>{email}</strong>, then your new password.</p>
        {/* hidden but present: a password manager needs to know which
            account this new password belongs to, or it saves a second
            entry instead of updating the first */}
        <input type="email" value={email} readOnly autoComplete="username"
               name="username" tabIndex={-1} aria-hidden="true" className="offscreen" />
        {CodeField}
        {NewPassword}
        <Notice kind="error">{error}</Notice>
        <button className="btn btn-primary btn-block" disabled={busy || token.length < 6}
                style={{ marginTop: ".6rem" }}>
          {busy ? "Saving…" : "Set password and sign in"}
        </button>
        <Links>
          <button type="button" className="btn-link" onClick={() => go("forgot")}>
            Send another code
          </button>
        </Links>
      </form>
    ),

  };

  return (
    <div className="centre">
      <div className="centre-box">
        <span><LockupStacked h={50} /></span>
        {screens[stage]}
        <p className="muted" style={{ fontSize: ".625rem", marginTop: "1.4rem",
                                      letterSpacing: ".1em", textTransform: "uppercase" }}>
          build {typeof __BUILD__ !== "undefined" ? __BUILD__ : "dev"}
        </p>
      </div>
    </div>
  );
}

/** Supabase's messages are written for developers. These are for golfers. */
function friendly(err) {
  const m = err?.message ?? String(err);
  if (/invalid login credentials/i.test(m)) return "That email and password don't match.";
  if (/user already registered/i.test(m)) return "There's already an account with that email. Try signing in.";
  if (/expired|invalid.*token|otp/i.test(m)) return "That code didn't work. It expires after an hour and works once — ask for a new one.";
  if (/rate limit|too many/i.test(m)) return "Too many attempts. Give it a minute and try again.";
  if (/password should be/i.test(m)) return `Use at least ${MIN} characters.`;
  if (/signups not allowed/i.test(m)) return "No account with that email. Create one first.";
  if (/not_invited/i.test(m))
    return "That email hasn't been invited yet. Ask your society organiser to add it — " +
           "they'll need the address you're using here. If they gave you a society code, " +
           "enter it above.";
  return m;
}
